import React from 'react'
import styled from 'styled-components'

import {
  formatMetricValue,
  formatMinMaxRange,
  getSourceColor,
} from '../utils/zoningHelpers'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  DifferenceCell,
  AuthorizedValue,
} from './ZoneMetricsTable.styles'

const MetricRow = styled.tr<{ $selected?: boolean; $editMode?: boolean }>`
  background-color: ${({ $selected, $editMode, theme }) =>
    $selected && $editMode
      ? theme.colors.primaryLight || 'rgba(0, 122, 255, 0.05)'
      : 'transparent'};

  td {
    border-bottom: none; // Parent metrics have no border (grouped with child)
  }

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.background.secondary || 'rgba(0, 0, 0, 0.02)'};
  }
`

const MetricSelector = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'inline-flex' : 'none')};
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.small};

  input[type='radio'] {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
`

const MetricNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding-top: ${({ theme }) => theme.spacing.small};
`

const EditableInput = styled.input<{
  $hasOverride?: boolean
  $isPending?: boolean
}>`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ $hasOverride, $isPending, theme }) =>
    $isPending
      ? theme.colors.primary
      : $hasOverride
        ? '#00B341'
        : theme.colors.text.primary};
  font-weight: ${({ $hasOverride, $isPending }) =>
    $hasOverride || $isPending ? '600' : '400'};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.background.secondary};
    cursor: not-allowed;
  }
`

export interface MetricGroupRowProps {
  metricKey: string
  metricLabel: string
  metricDescription?: string
  groupName: 'group1' | 'group2'
  isSelected: boolean
  isEditing: boolean

  // Values
  currentValue?: number
  minValue?: number
  maxValue?: number

  // Sources
  minSource?: string
  maxSource?: string
  minReference?: any
  maxReference?: any

  // State
  isOverridden: boolean
  isPending: boolean
  unit?: string

  // Handlers
  onGroupChange: (group: 'group1' | 'group2', value: string) => void
  onValueChange: (metricKey: string, value: number) => void
  onAuthorizedClick: (metricKey: string) => void
  status?: 'found' | 'not_found' | 'not_applicable' | 'reference'
}

/**
 * Row component for exclusive metric groups (IOS/COS, IUS/CUS/IBUS/IM)
 * Shows radio selector in edit mode, clickable authorized value
 */
export const MetricGroupRow: React.FC<MetricGroupRowProps> = ({
  metricKey,
  metricLabel,
  // metricDescription,
  groupName,
  isSelected,
  isEditing,
  currentValue,
  minValue,
  maxValue,
  // minSource,
  maxSource,
  // minReference,
  // maxReference,
  isOverridden,
  isPending,
  unit,
  onGroupChange,
  onValueChange,
  onAuthorizedClick,
  status,
}) => {
  // Calculate difference
  const calculateDifference = (): {
    value: number
    isPositive: boolean
  } | null => {
    if (!maxValue) {
      return null
    }

    if (maxValue === undefined || currentValue === undefined) {
      return null
    }

    const diff = maxValue - currentValue

    return { value: diff, isPositive: diff >= 0 }
  }

  const diff = calculateDifference()

  // Format authorized value
  const formatAuthorized = (): string => {
    if (status === 'not_applicable' && !isEditing && !minValue && !maxValue) {
      return 'N/A'
    }
    if (minValue !== undefined && maxValue !== undefined) {
      return formatMinMaxRange(minValue, maxValue, unit)
    }
    if (maxValue !== undefined) {
      return formatMetricValue(maxValue, unit, metricKey)
    }
    if (minValue !== undefined) {
      return formatMetricValue(minValue, unit, metricKey)
    }
    return '-'
  }

  const authorizedDisplay = formatAuthorized()
  const sourceColor = getSourceColor(maxSource, isOverridden)

  return (
    <MetricRow $selected={isSelected} $editMode={isEditing}>
      <td>
        <MetricNameContainer>
          <MetricSelector $visible={isEditing}>
            <input
              type="radio"
              name={`metric-${groupName}`}
              value={metricKey}
              checked={isSelected}
              onChange={(e) => onGroupChange(groupName, e.target.value)}
            />
          </MetricSelector>
          <MetricLabel>{metricLabel}</MetricLabel>
        </MetricNameContainer>
      </td>

      <MetricValueCell>
        {currentValue !== undefined ? (
          formatMetricValue(currentValue, unit, metricKey)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <MetricValueCell>
        {isEditing ? (
          <EditableInput
            type="number"
            step="0.01"
            value={maxValue !== undefined ? maxValue : ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value)
              if (!isNaN(value)) {
                onValueChange(`${metricKey}_max`, value)
              }
            }}
            $hasOverride={isOverridden}
            $isPending={isPending}
            placeholder="0.00"
          />
        ) : authorizedDisplay !== '-' ? (
          <AuthorizedValue
            $source={maxSource}
            $isUserEdited={isOverridden}
            $isPending={isPending}
            onClick={() => onAuthorizedClick(metricKey)}
            style={{ cursor: 'pointer', color: sourceColor }}
            title="Click to view source details"
          >
            {authorizedDisplay}
          </AuthorizedValue>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <DifferenceCell $isPositive={diff?.isPositive ?? true}>
        {diff ? (
          <>
            {diff.isPositive && '+'}
            {formatMetricValue(diff.value, unit, metricKey)}
          </>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </DifferenceCell>
    </MetricRow>
  )
}
